import { useState } from "react";
import {
  ActionIcon,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import {
  IconArrowDown,
  IconArrowUp,
  IconCategory,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createSupplyCategory,
  deleteSupplyCategory,
  updateSupplyCategory,
} from "@/api/supply";
import { ModalTitle } from "@/components/ModalTitle";
import { notify } from "@/lib/notify";

export function CategoryManager({ opened, onClose, categories, itemCount }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["supply-categories"] });
    queryClient.invalidateQueries({ queryKey: ["supply-items"] });
  };

  const create = useMutation({
    mutationFn: createSupplyCategory,
    onSuccess: () => {
      refresh();
      setDraft("");
    },
    onError: (error) => notify.error(error),
  });

  const rename = useMutation({
    mutationFn: ({ id, name }) => updateSupplyCategory(id, { name }),
    onSuccess: refresh,
    onError: (error) => notify.error(error),
  });

  const reorder = useMutation({
    mutationFn: (ordered) =>
      Promise.all(
        ordered
          .map((category, index) => ({ category, rank: index + 1 }))
          .filter(({ category, rank }) => category.rank !== rank)
          .map(({ category, rank }) =>
            updateSupplyCategory(category.id, { rank }),
          ),
      ),
    onSuccess: refresh,
    onError: (error) => notify.error(error),
  });

  const remove = useMutation({
    mutationFn: deleteSupplyCategory,
    onSuccess: () => {
      refresh();
      notify.success("Kategori silindi");
    },
    onError: (error) => notify.error(error),
  });

  const move = (index, step) => {
    const ordered = [...categories];
    const [moved] = ordered.splice(index, 1);

    ordered.splice(index + step, 0, moved);
    reorder.mutate(ordered);
  };

  const confirmDelete = (category) =>
    modals.openConfirmModal({
      title: <ModalTitle icon={IconTrash} title="Kategoriyi sil" />,
      children: (
        <Text size="sm">
          <strong>{category.name}</strong> silinir.{" "}
          {itemCount(category.id)
            ? `İçindeki ${itemCount(category.id)} kalem silinmez, "Diğer" altına düşer.`
            : "İçinde kalem yok."}
        </Text>
      ),
      labels: { confirm: "Sil", cancel: "Vazgeç" },
      confirmProps: { color: "red" },
      onConfirm: () => remove.mutate(category.id),
    });

  const add = (event) => {
    event.preventDefault();

    if (draft.trim()) {
      create.mutate({ name: draft.trim() });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<ModalTitle icon={IconCategory} title="Kalem kategorileri" />}
    >
      <Stack gap="sm">
        <Text size="xs" c="dimmed">
          Talep ekranında kalemler bu sırayla, kategori başlıkları altında
          listelenir. Adı değiştirmek için üzerine yazıp alandan çık.
        </Text>

        {categories.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="sm">
            Henüz kategori yok
          </Text>
        ) : (
          categories.map((category, index) => (
            <Group key={category.id} gap={6} wrap="nowrap">
              <TextInput
                style={{ flex: 1 }}
                defaultValue={category.name}
                aria-label="Kategori adı"
                rightSection={
                  <Text size="xs" c="dimmed">
                    {itemCount(category.id)}
                  </Text>
                }
                onBlur={(event) => {
                  const name = event.currentTarget.value.trim();

                  if (name && name !== category.name) {
                    rename.mutate({ id: category.id, name });
                  }
                }}
              />
              <Tooltip label="Yukarı">
                <ActionIcon
                  variant="subtle"
                  disabled={index === 0 || reorder.isPending}
                  onClick={() => move(index, -1)}
                >
                  <IconArrowUp size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Aşağı">
                <ActionIcon
                  variant="subtle"
                  disabled={
                    index === categories.length - 1 || reorder.isPending
                  }
                  onClick={() => move(index, 1)}
                >
                  <IconArrowDown size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Sil">
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={() => confirmDelete(category)}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
          ))
        )}

        <form onSubmit={add}>
          <Group gap="sm" wrap="nowrap" mt="xs">
            <TextInput
              style={{ flex: 1 }}
              placeholder="Yeni kategori (ör. Temizlik ürünleri)"
              value={draft}
              onChange={(event) => setDraft(event.currentTarget.value)}
            />
            <Button
              type="submit"
              leftSection={<IconPlus size={16} />}
              loading={create.isPending}
              disabled={!draft.trim()}
            >
              Ekle
            </Button>
          </Group>
        </form>
      </Stack>
    </Modal>
  );
}
